/**
 * Comment moderation: how long members may edit or delete their own comments.
 *
 * The policy is org-wide and lives in the organization's general settings
 * (`commentEditPolicy`, `commentDeletePolicy`). ThreadService enforces it on
 * every edit/delete path (web, portal, mobile, MCP and Benny all go through
 * it); clients use the same evaluators to hide actions the server would
 * reject. Org owners and admins bypass the policy so they can still moderate.
 */

/** How long after posting a member may edit their own comment. */
export enum CommentEditPolicyEnum {
  ONE_MINUTE = 'ONE_MINUTE',
  ALWAYS = 'ALWAYS',
  NEVER = 'NEVER',
}

/** How long after posting a member may delete their own comment. */
export enum CommentDeletePolicyEnum {
  ONE_MINUTE = 'ONE_MINUTE',
  ALWAYS = 'ALWAYS',
  NEVER = 'NEVER',
  /** Allowed at any time unless the comment has replies. */
  NO_REPLIES = 'NO_REPLIES',
}

export const DEFAULT_COMMENT_EDIT_POLICY = CommentEditPolicyEnum.ONE_MINUTE
export const DEFAULT_COMMENT_DELETE_POLICY = CommentDeletePolicyEnum.ONE_MINUTE

/** Length of the ONE_MINUTE window. */
export const COMMENT_MODERATION_WINDOW_MS = 60 * 1000

/**
 * Server-side slack past the window, so a save sent just as the window closes
 * (network latency, a few seconds of typing) is not rejected. Clients hide the
 * actions at the exact window.
 */
export const COMMENT_MODERATION_GRACE_MS = 10 * 1000

export interface CommentModerationPolicy {
  editPolicy: CommentEditPolicyEnum
  deletePolicy: CommentDeletePolicyEnum
}

/** What the comment-policy endpoints return for the current viewer. */
export interface CommentModerationContext extends CommentModerationPolicy {
  /** Org owners and admins are not bound by the policy. */
  canBypass: boolean
  /** Server clock at response time, so clients can correct for clock skew. */
  serverTime: string
}

export const DEFAULT_COMMENT_MODERATION_POLICY: CommentModerationPolicy = {
  editPolicy: DEFAULT_COMMENT_EDIT_POLICY,
  deletePolicy: DEFAULT_COMMENT_DELETE_POLICY,
}

/** Personal (family) spaces have no moderation settings and are not restricted. */
export const PERSONAL_COMMENT_MODERATION_POLICY: CommentModerationPolicy = {
  editPolicy: CommentEditPolicyEnum.ALWAYS,
  deletePolicy: CommentDeletePolicyEnum.ALWAYS,
}

const EDIT_POLICIES = new Set<string>(Object.values(CommentEditPolicyEnum))
const DELETE_POLICIES = new Set<string>(Object.values(CommentDeletePolicyEnum))

export function isCommentEditPolicy(
  value: unknown,
): value is CommentEditPolicyEnum {
  return typeof value === 'string' && EDIT_POLICIES.has(value)
}

export function isCommentDeletePolicy(
  value: unknown,
): value is CommentDeletePolicyEnum {
  return typeof value === 'string' && DELETE_POLICIES.has(value)
}

/**
 * Read the effective policy from an organization's settings JSON. Unset or
 * unrecognized values fall back to the defaults; personal accounts are never
 * restricted because the settings are hidden from them.
 */
export function resolveCommentModerationPolicy(
  settings: unknown,
  accountType?: string | null,
): CommentModerationPolicy {
  if (accountType === 'PERSONAL') return PERSONAL_COMMENT_MODERATION_POLICY
  const general = (settings as { general?: Record<string, unknown> } | null)
    ?.general
  const editPolicy = general?.commentEditPolicy
  const deletePolicy = general?.commentDeletePolicy
  return {
    editPolicy: isCommentEditPolicy(editPolicy)
      ? editPolicy
      : DEFAULT_COMMENT_EDIT_POLICY,
    deletePolicy: isCommentDeletePolicy(deletePolicy)
      ? deletePolicy
      : DEFAULT_COMMENT_DELETE_POLICY,
  }
}

export type CommentModerationDecision =
  | {
      allowed: true
      /** When the action stops being allowed; null when it never expires. */
      expiresAt: Date | null
    }
  | { allowed: false; reason: string }

interface CommentModerationInput {
  createdAt: Date | string
  /** Defaults to the current time. */
  now?: Date | number
  canBypass?: boolean
  /** Extra time allowed past the window (server only). */
  graceMs?: number
}

function windowDecision(
  createdAt: Date | string,
  now: Date | number | undefined,
  graceMs: number,
  expiredReason: string,
): CommentModerationDecision {
  const created = new Date(createdAt).getTime()
  // An unparseable timestamp can't be placed in the window; fail closed.
  if (Number.isNaN(created)) return { allowed: false, reason: expiredReason }
  const nowMs = now === undefined ? Date.now() : new Date(now).getTime()
  const expiresAtMs = created + COMMENT_MODERATION_WINDOW_MS
  if (nowMs >= expiresAtMs + graceMs) {
    return { allowed: false, reason: expiredReason }
  }
  return { allowed: true, expiresAt: new Date(expiresAtMs) }
}

export function evaluateCommentEdit(
  policy: Pick<CommentModerationPolicy, 'editPolicy'>,
  input: CommentModerationInput,
): CommentModerationDecision {
  if (input.canBypass) return { allowed: true, expiresAt: null }
  switch (policy.editPolicy) {
    case CommentEditPolicyEnum.ALWAYS:
      return { allowed: true, expiresAt: null }
    case CommentEditPolicyEnum.NEVER:
      return {
        allowed: false,
        reason: "Your organization doesn't allow editing comments",
      }
    case CommentEditPolicyEnum.ONE_MINUTE:
    default:
      return windowDecision(
        input.createdAt,
        input.now,
        input.graceMs ?? 0,
        'Comments can only be edited within 1 minute of posting',
      )
  }
}

export function evaluateCommentDelete(
  policy: Pick<CommentModerationPolicy, 'deletePolicy'>,
  input: CommentModerationInput & { hasReplies: boolean },
): CommentModerationDecision {
  if (input.canBypass) return { allowed: true, expiresAt: null }
  switch (policy.deletePolicy) {
    case CommentDeletePolicyEnum.ALWAYS:
      return { allowed: true, expiresAt: null }
    case CommentDeletePolicyEnum.NEVER:
      return {
        allowed: false,
        reason: "Your organization doesn't allow deleting comments",
      }
    case CommentDeletePolicyEnum.NO_REPLIES:
      return input.hasReplies
        ? { allowed: false, reason: "Comments with replies can't be deleted" }
        : { allowed: true, expiresAt: null }
    case CommentDeletePolicyEnum.ONE_MINUTE:
    default:
      return windowDecision(
        input.createdAt,
        input.now,
        input.graceMs ?? 0,
        'Comments can only be deleted within 1 minute of posting',
      )
  }
}

export const COMMENT_EDIT_POLICY_OPTIONS: readonly {
  value: CommentEditPolicyEnum
  label: string
}[] = [
  {
    value: CommentEditPolicyEnum.ONE_MINUTE,
    label: 'Allow edits for 1 minute',
  },
  { value: CommentEditPolicyEnum.ALWAYS, label: 'Allow edits at any time' },
  { value: CommentEditPolicyEnum.NEVER, label: 'Do not allow edits' },
]

export const COMMENT_DELETE_POLICY_OPTIONS: readonly {
  value: CommentDeletePolicyEnum
  label: string
}[] = [
  {
    value: CommentDeletePolicyEnum.ONE_MINUTE,
    label: 'Allow deletes for 1 minute',
  },
  { value: CommentDeletePolicyEnum.ALWAYS, label: 'Allow deletes at any time' },
  { value: CommentDeletePolicyEnum.NEVER, label: 'Do not allow deletes' },
  {
    value: CommentDeletePolicyEnum.NO_REPLIES,
    label: 'Do not allow deletion if the comment has replies',
  },
]

/** When each comment's ONE_MINUTE window closes, for policies that use one. */
export function getCommentModerationDeadlines(
  policy: CommentModerationContext | undefined,
  createdAts: readonly (Date | string | null | undefined)[],
): number[] {
  const effective = policy ?? DEFAULT_COMMENT_MODERATION_POLICY
  if (policy?.canBypass) return []
  const usesWindow =
    effective.editPolicy === CommentEditPolicyEnum.ONE_MINUTE ||
    effective.deletePolicy === CommentDeletePolicyEnum.ONE_MINUTE
  if (!usesWindow) return []
  const deadlines: number[] = []
  for (const createdAt of createdAts) {
    if (!createdAt) continue
    const created = new Date(createdAt).getTime()
    if (!Number.isNaN(created)) {
      deadlines.push(created + COMMENT_MODERATION_WINDOW_MS)
    }
  }
  return deadlines
}

export interface CommentActionAvailability {
  canEdit: boolean
  canDelete: boolean
}

/**
 * Whether the viewer may edit/delete a comment under the moderation policy.
 * Only authors can edit or delete; until the policy loads the default
 * (one-minute, no bypass) applies, matching the server's default.
 */
export function getCommentActionAvailability({
  policy,
  isAuthor,
  createdAt,
  totalReplies,
  now,
}: {
  policy: CommentModerationContext | undefined
  isAuthor: boolean
  createdAt: Date | string
  totalReplies?: number | null
  now: number
}): CommentActionAvailability {
  if (!isAuthor) return { canEdit: false, canDelete: false }
  const effective = policy ?? DEFAULT_COMMENT_MODERATION_POLICY
  const canBypass = policy?.canBypass ?? false
  return {
    canEdit: evaluateCommentEdit(effective, { createdAt, now, canBypass })
      .allowed,
    canDelete: evaluateCommentDelete(effective, {
      createdAt,
      now,
      canBypass,
      hasReplies: (totalReplies ?? 0) > 0,
    }).allowed,
  }
}
